<script>
import countries from '@shared/assets/data/countries.json';
import countries_by_code from '@shared/assets/data/countries_by_code.json';
import utilsMixin from '@shared/mixins/utils';
import StripeElementsSplitted from '@shared/components/StripeElementsSplitted.vue';
import BasePasswordInput from '@shared/components/BasePasswordInput.vue';
import APIStore from '@school/services/API/Store';
import APICustomer from '@school/services/API/Customer';

export default {
  mixins: [utilsMixin],
  inject: ['store'],
  components: { StripeElementsSplitted, BasePasswordInput },
  props: {
    isLoading: {
      type: Boolean,
      required: true,
    },
    tempCustomer: {
      type: Object,
      default: () => false,
    },
    paymentPlan: {
      type: Object,
      default: null,
    },
    buyable: {
      type: Object,
      required: true,
    },
    basePrice: {
      type: Number,
      required: true,
    },
    selectedPriceUuid: {
      type: String,
      default: null,
    },
  },
  static: {
    countries,
  },
  data() {
    const price = this.paymentPlan ? this.paymentPlan.amount : this.basePrice;

    return {
      stripeError: null,
      stripeIsCompleted: false,
      discountIsLoading: false,
      discountIsApplied: false,
      hasChosenPaypal: false,
      hasAddressOpen: false,
      isFree: price <= 0,
      siretError: null,
      vatError: null,
      customFieldErrors: {},
      customer: {
        discount_code: '',
        firstname: '',
        lastname: '',
        password: '',
        name: '',
        address_line_1: '',
        address_line_2: '',
        city: '',
        postal_code: '',
        state: '',
        country_code: 'FR',
        siret: '',
        intracom_vat: '',
        custom_fields_data: [],
      },
      customFieldsAnswers: {},
      isCompany: false,
    };
  },
  created() {
    // Pre-initialize keys so Vue 2 reactivity tracks them
    // (avoids adding non-reactive properties at runtime via v-model).
    const initial = {};
    const errors = {};
    for (const field of this.customFieldsList) {
      initial[field.name] = field.type === 'checkbox' ? false : '';
      errors[field.name] = '';
    }
    this.customFieldsAnswers = initial;
    this.customFieldErrors = errors;
  },
  computed: {
    authCustomer() {
      return this.$store.getters['auth/customer'];
    },
    customFieldsList() {
      const opts = this.store?.custom_fields_options;
      if (!opts?.enabled) return [];
      const c = this.authCustomer;
      // Existing customers (created before the fields were configured) are exempt.
      // A brand-new registration has no authCustomer yet => fields shown.
      if (opts.configured_at && c && c.created_at && c.created_at < opts.configured_at) {
        return [];
      }
      const fields = opts.fields || [];
      // Hide store fields the customer has already answered on a previous purchase.
      const answered = ((c && c.custom_fields_data) || []).map((a) => a.name);
      return fields.filter((f) => !answered.includes(f.name));
    },
    submitIsDisabled() {
      return (
        (!this.isFree && !this.stripeIsCompleted)
        || (this.customer.discount_code !== '' && !this.discountIsApplied)
        || this.customFieldsRequiredMissing
      );
    },
    customFieldsRequiredMissing() {
      return this.customFieldsList.some((f) => {
        if (!f.required) return false;
        const raw = this.customFieldsAnswers[f.name];
        // A required checkbox (e.g. consent) must be explicitly checked.
        if (f.type === 'checkbox') return raw !== true;
        return raw === '' || raw === null || raw === undefined;
      });
    },
    canUseCoupon() {
      return (
        !this.isFree
        && (this.buyable.discount_price == null || this.paymentPlan)
      );
    },
    canUsePayPal() {
      return (
        !this.isFree
        && !this.paymentPlan
        && this.store.can_accept_paypal
        && !this.store.feature_options.paypal.disabled
      );
    },
    countryList() {
      return this.store.feature_options.language.language_option !== 'fr'
        ? Object.entries(countries_by_code)
          .sort(([, value1], [, value2]) => value1.name.localeCompare(value2.name))
          .reduce((acc, [key, value]) => {
            acc[key] = value.name;
            return acc;
          }, {})
        : countries;
    },
  },
  watch: {
    isFree(newValue) {
      if (newValue) {
        this.customer.address_line_1 = '';
        this.customer.address_line_2 = '';
        this.customer.city = '';
        this.customer.postal_code = '';
        this.customer.state = '';
      }
    },
    'customer.intracom_vat': function(newValue) {
      if (newValue) {
        // Normalize: remove spaces and force uppercase for all characters
        const normalized = String(newValue).replace(/\s/g, '').toUpperCase();
        if (normalized !== newValue) {
          this.customer.intracom_vat = normalized;
        }
        // Validation remains on blur
      }
    },
    'customer.siret': function(newValue) {
      if (newValue) {
        // Remove all spaces first
        let formatted = newValue.replace(/\s/g, '');
        // Format with spaces: XXX XXX XXX XXXXX
        if (formatted.length <= 3) {
          // No formatting needed yet
        } else if (formatted.length <= 6) {
          formatted = `${formatted.substring(0, 3)} ${formatted.substring(3)}`;
        } else if (formatted.length <= 9) {
          formatted = `${formatted.substring(0, 3)} ${formatted.substring(
            3,
            6,
          )} ${formatted.substring(6)}`;
        } else {
          formatted = `${formatted.substring(0, 3)} ${formatted.substring(
            3,
            6,
          )} ${formatted.substring(6, 9)} ${formatted.substring(9)}`;
        }
        // Only update if the format has changed to avoid infinite loop
        if (formatted !== newValue) {
          this.customer.siret = formatted;
        }

        // Remove validation from here - will be done on blur
      }
    },
    customer: {
      deep: true,
      handler() {
        this.$emit('update', this.customer);
      },
    },
    customFieldsAnswers: {
      deep: true,
      handler(newAnswers) {
        const payload = [];
        for (const field of this.customFieldsList) {
          const raw = newAnswers[field.name];
          if (field.type === 'checkbox') {
            payload.push({
              name: field.name,
              answer: { value: raw ? 'true' : 'false', label: raw ? this.$t('yes') : this.$t('no') },
            });
          } else if (raw !== '' && raw !== null && raw !== undefined) {
            payload.push({
              name: field.name,
              // label truncated to respect answer.label max length (backend keeps the question label).
              answer: { value: String(raw), label: String(raw).slice(0, 255) },
            });
          }
        }
        this.customer.custom_fields_data = payload;
      },
    },
    paymentPlan(value) {
      if (value) {
        this.$nextTick(() => (this.hasChosenPaypal = false));
      }

      if (this.customer.discount_code) {
        this.applyCoupon();
      }
    },
    basePrice(newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }

      if (!this.paymentPlan) {
        this.isFree = newValue <= 0;
      }

      if (this.customer.discount_code && !this.discountIsLoading) {
        this.applyCoupon();
      }
    },
    selectedPriceUuid(newValue, oldValue) {
      if (newValue === oldValue) {
        return;
      }

      if (this.customer.discount_code) {
        if (!this.discountIsLoading) {
          this.applyCoupon();
        }
      } else {
        this.$emit('new-price');
        this.discountIsApplied = false;
        if (!this.paymentPlan) {
          this.isFree = this.basePrice <= 0;
        }
      }
    },
  },
  methods: {
    validateSiret() {
      if (this.customer.siret) {
        const siretRegex = /^\d{3}\s\d{3}\s\d{3}\s\d{5}$/;
        const digitsOnly = this.customer.siret.replace(/\s/g, '');

        if (!siretRegex.test(this.customer.siret) || digitsOnly.length !== 14) {
          this.siretError = this.$t('siret_validation_error');
        } else {
          this.siretError = null;
        }
      } else {
        this.siretError = null;
      }
    },
    validateVat() {
      if (this.customer.intracom_vat) {
        // Format simplifié: code pays (2 lettres) suivi d'au moins un caractère
        const vatRegex = /^[A-Z]{2}[A-Z0-9]+$/;
        if (!vatRegex.test(this.customer.intracom_vat.replace(/\s/g, ''))) {
          this.vatError = this.$t('vat_validation_error');
        } else {
          this.vatError = null;
        }
      } else {
        this.vatError = null;
      }
    },
    validateCustomField(field) {
      const raw = this.customFieldsAnswers[field.name];
      const missing = field.type === 'checkbox'
        ? raw !== true
        : (raw === '' || raw === null || raw === undefined);
      const error = field.required && missing ? this.$t('field_required') : '';
      this.$set(this.customFieldErrors, field.name, error);
    },

    applyCoupon() {
      const payment_schedule_id = this.paymentPlan
        ? this.paymentPlan.id
        : undefined;
      const price = this.paymentPlan ? this.paymentPlan.amount : this.basePrice;

      this.discountIsLoading = true;
      this.discountIsApplied = false;
      this.isFree = price < 0;
      this.$emit('new-price');

      APIStore.getPricingData({
        payment_schedule_id,
        ...(this.buyable.buyable_type === 'TRAINING'
          ? { training_uuid: this.buyable.uuid }
          : {}),
        ...(this.buyable.buyable_type === 'BUNDLE'
          ? { bundle_uuid: this.buyable.uuid }
          : {}),
        ...(this.buyable.buyable_type === 'MEMBERSHIP_PRICE'
          ? { membership_price_uuid: this.selectedPriceUuid || this.buyable.uuid }
          : {}),
        discount_code: this.customer.discount_code,
      })
        .then((data) => {
          data = data.data;
          this.discountIsApplied = !!data.additional_data.applied_discount_code;

          if (this.discountIsApplied) {
            this.$emit('new-price', data.applicable_price);
            // No need to emit original price for MEMBERSHIP_PRICE - frontend has direct access to buyable.amount
            this.isFree = data.applicable_price <= 0;
          } else {
            this.customer.discount_code = '';
            this.$buefy.toast.open({
              type: 'is-danger',
              message: this.$t('promo_code_not_usable'),
            });
          }
        })
        .finally(() => (this.discountIsLoading = false));
    },
    payWithPayPal() {
      if (!this.theForm?.checkValidity() || !this.tempCustomer?.uuid) {
        this.$buefy.dialog.alert(this.$t('missing_information_for_payment'));
        return;
      }

      if (this.customer.discount_code !== '' && !this.discountIsApplied) {
        this.$buefy.dialog.alert({
          message: this.$t('promo_code_not_applied'),
          hasIcon: true,
          icon: 'exclamation-triangle',
          iconPack: 'fas',
        });
        return;
      }

      const w = 520;
      const h = window.innerHeight * 0.7;
      const x = (window.screen.width - w) / 2;
      const y = 100;
      const loader = this.$buefy.loading.open();
      let orderId;

      if (this.popup) {
        this.popup.close();
        this.popup = null;
      }

      this.popup = window.open(
        `${window.location.protocol}//${window.location.hostname}/paypal_init.html`,
        '_blank',
        `popup=1,toolbar=no,menubar=no,width=${w},height=${h},left=${x},top=${y}`,
      );

      if (this.popup == null) {
        this.showPopupBlockedDialog(
          this.$t('paypal'),
          this.$t('use_credit_card_if_not_working'),
        );
        loader.close();
        return;
      }

      APICustomer.getPayPalOrder({
        customer_uuid: this.tempCustomer.uuid,
        ...(this.buyable.buyable_type === 'TRAINING'
          ? { training_uuid: this.buyable.uuid }
          : {}),
        ...(this.buyable.buyable_type === 'BUNDLE'
          ? { bundle_uuid: this.buyable.uuid }
          : {}),
        ...(this.buyable.buyable_type === 'MEMBERSHIP_PRICE'
          ? { membership_price_uuid: this.selectedPriceUuid || this.buyable.uuid }
          : {}),
        discount_code: this.customer.discount_code || undefined,
      })
        .then(({ data: { id, links = [] } = {} }) => {
          orderId = id;
          const url = links.find((v) => v.rel === 'approve')?.href;
          const { popup } = this;

          if (!popup || popup.closed) {
            return;
          }

          popup.location.href = url;
          popup.focus();

          const onPayPalMessage = (event) => {
            if (
              (event.origin !== (window.origin || window.location.origin)
                && event.source !== window)
              || event.data?.name !== 'teachizy-paypal-popup'
            ) {
              return;
            }

            if (!popup.closed) {
              this.$emit('paypal-order', orderId);
              popup.close();
            }

            window.removeEventListener('message', onPayPalMessage);
          };

          window.addEventListener('message', onPayPalMessage);
        })
        .finally(() => loader.close());
    },
  },
  mounted() {
    this.theForm = this.$el.closest('form');
  },
  i18n: {
    messages: {
      en: {
        firstname: 'First name',
        lastname: 'Last name',
        indicate_billing_address: 'Enter your billing address',
        company_name_optional: 'Company / Name (optional)',
        address_line_1: 'Address (number + street)',
        address_line_2: 'Additional address information',
        city: 'City',
        postal_code: 'Postal code',
        state_optional: 'State/Region (optional)',
        set_your_password: 'Set your password',
        promo_code: 'Promo code',
        apply: 'Apply',
        promo_code_applied: 'The promo code has been successfully applied!',
        credit_card: 'Credit card',
        paypal: 'Paypal',
        siret_number: 'SIRET number',
        siret_validation_error:
          'SIRET must be 14 digits in format: XXX XXX XXX XXXXX',
        vat_number: 'Intra-community VAT number (e.g. FR85123456789)',
        vat_validation_error: 'The VAT number must start with the country code (e.g. FR, BE), followed by numbers and/or letters.',
        enter_credit_card: 'Enter your credit card details',
        access_with_paypal: 'Access with <strong>PayPal</strong>',
        access_training: 'Access the training',
        access_subscription: 'Access the membership',
        access_pack: 'Access the bundle',
        secure_registration: 'SSL secure registration',
        promo_code_not_usable: 'This promo code cannot be used.',
        missing_information_for_payment:
          'There is missing information in the form to proceed with payment.',
        promo_code_not_applied:
          'You have entered a promo code, but you haven\'t applied it. Please click on "apply".',
        use_credit_card_if_not_working:
          "You can use credit card payment if this doesn't work.",
        buy_as_company: 'Buy as a company',
        field_required: 'This field is required.',
        select_placeholder: '-- Select --',
        yes: 'Yes',
        no: 'No',
      },
      fr: {
        firstname: 'Prénom',
        lastname: 'Nom',
        indicate_billing_address: 'Entrez votre adresse de facturation',
        company_name_optional: 'Société / Nom (facultatif)',
        address_line_1: 'Adresse (numéro + rue)',
        address_line_2: "Complément d'adresse",
        city: 'Ville',
        postal_code: 'Code postal',
        state_optional: 'État/Région (facultatif)',
        set_your_password: 'Définissez votre mot de passe',
        promo_code: 'Code promo',
        apply: 'Appliquer',
        promo_code_applied: 'Le code promo a été appliqué !',
        credit_card: 'Carte de crédit',
        paypal: 'Paypal',
        siret_number: 'N° SIRET',
        siret_validation_error:
          'Le SIRET doit comporter 14 chiffres au format : XXX XXX XXX XXXXX',
        vat_number: 'N° de TVA intracommunautaire (ex: FR85123456789)',
        vat_validation_error: 'Le numéro de TVA doit commencer par le code pays (ex. FR, BE), suivi de chiffres et/ou de lettres.',
        enter_credit_card: 'Entrez les détails de votre carte de crédit',
        access_with_paypal: 'Accéder avec <strong>PayPal</strong>',
        access_training: 'Accéder à la formation',
        access_subscription: "Accéder à l'abonnement",
        access_pack: 'Accéder au pack',
        secure_registration: 'Inscription sécurisée avec SSL',
        promo_code_not_usable: "Ce code promo n'est pas utilisable",
        missing_information_for_payment:
          'Il manque des informations dans le formulaire pour procéder au paiement.',
        promo_code_not_applied:
          'Vous avez saisi un code promo, mais vous ne l\'avez pas appliqué. Veuillez cliquer sur "appliquer".',
        use_credit_card_if_not_working:
          'Vous pouvez utiliser le paiement par carte de crédit si cela ne fonctionne pas.',
        buy_as_company: 'Acheter en tant qu\'entreprise',
        field_required: 'Ce champ est obligatoire.',
        select_placeholder: '-- Sélectionner --',
        yes: 'Oui',
        no: 'Non',
      },
    },
  },
};
</script>

<template>
  <div class="columns is-multiline">
    <div class="column is-12 pb-0">
      <b-field grouped>
        <b-field
          v-if="!tempCustomer || !tempCustomer.firstname"
          class="w-full is-flex-shrink-1"
        >
          <b-input
            v-model="customer.firstname"
            :placeholder="$t('firstname')"
            expanded
            required
          />
        </b-field>
        <b-field
          v-if="!tempCustomer || !tempCustomer.firstname"
          class="w-full is-flex-shrink-1"
        >
          <b-input
            v-model="customer.lastname"
            :placeholder="$t('lastname')"
            expanded
          />
        </b-field>
      </b-field>
    </div>
    <div
      v-if="!isFree && buyable.feature_options.billing_address.enabled"
      class="column is-12 pb-0"
    >
      <h3 class="mb-2">
        {{ $t("indicate_billing_address") }}
      </h3>

      <div class="mb-4">
        <b-checkbox v-model="isCompany">
          {{ $t('buy_as_company') }}
        </b-checkbox>
      </div>

      <template v-if="isCompany">
        <b-field>
          <b-input
            v-model.trim="customer.name"
            :placeholder="$t('company_name_optional')"
          />
        </b-field>
        <b-field :type="{ 'is-danger': siretError }" :message="siretError">
          <b-input
            v-model.trim="customer.siret"
            :placeholder="$t('siret_number')"
            @blur="validateSiret"
          />
        </b-field>
        <b-field :type="{ 'is-danger': vatError }" :message="vatError">
          <b-input
            v-model.trim="customer.intracom_vat"
            :placeholder="$t('vat_number')"
            @blur="validateVat"
          />
        </b-field>
      </template>

      <b-field>
        <b-input
          v-model.trim="customer.address_line_1"
          :placeholder="$t('address_line_1')"
          required
        />
      </b-field>
      <b-field>
        <b-input
          v-model.trim="customer.address_line_2"
          :placeholder="$t('address_line_2')"
        />
      </b-field>
      <b-field grouped>
        <b-field class="w-full is-flex-shrink-1">
          <b-input
            v-model.trim="customer.city"
            :placeholder="$t('city')"
            required
          />
        </b-field>
        <b-field class="w-full is-flex-shrink-1">
          <b-input
            v-model.trim="customer.postal_code"
            :placeholder="$t('postal_code')"
            required
          />
        </b-field>
      </b-field>
      <b-field>
        <b-select v-model="customer.country_code" expanded required>
          <option
            v-for="(country, code) in countryList"
            :key="code"
            :value="code"
          >
            {{ country }}
          </option>
        </b-select>
      </b-field>
      <b-field v-show="customer.country_code != 'FR'">
        <b-input
          v-model.trim="customer.state"
          :placeholder="$t('state_optional')"
        />
      </b-field>
    </div>
    <div
      v-if="!tempCustomer || !tempCustomer.firstname"
      class="column is-12 pb-0"
    >
      <b-field :label="$t('set_your_password')">
        <BasePasswordInput v-model="customer.password" />
      </b-field>
    </div>
    <div
      v-for="field in customFieldsList"
      :key="`cf-${field.name}`"
      class="column is-12 pb-0"
    >
      <b-field
        :type="{ 'is-danger': customFieldErrors[field.name] }"
        :message="customFieldErrors[field.name]"
      >
        <template v-if="field.type !== 'checkbox'" #label>
          {{ field.label }}
          <span v-if="field.required" class="has-text-danger">*</span>
        </template>
        <b-input
          v-if="field.type === 'text'"
          v-model="customFieldsAnswers[field.name]"
          :required="field.required"
          maxlength="100"
          :has-counter="false"
          expanded
          @blur="validateCustomField(field)"
        />
        <b-input
          v-else-if="field.type === 'textarea'"
          v-model="customFieldsAnswers[field.name]"
          type="textarea"
          maxlength="1000"
          :has-counter="false"
          :required="field.required"
          expanded
          @blur="validateCustomField(field)"
        />
        <b-select
          v-else-if="field.type === 'select'"
          v-model="customFieldsAnswers[field.name]"
          :required="field.required"
          expanded
          @input="validateCustomField(field)"
        >
          <option value="" disabled>
            {{ $t('select_placeholder') }}
          </option>
          <option v-for="opt in field.options || []" :key="opt" :value="opt">
            {{ opt }}
          </option>
        </b-select>
        <b-checkbox
          v-else-if="field.type === 'checkbox'"
          v-model="customFieldsAnswers[field.name]"
          @input="validateCustomField(field)"
        >
          {{ field.label }}
          <span v-if="field.required" class="has-text-danger">*</span>
        </b-checkbox>
      </b-field>
    </div>
    <div v-if="canUseCoupon" class="column is-12 pb-0">
      <b-field>
        <b-input
          v-model.trim="customer.discount_code"
          :placeholder="$t('promo_code')"
          expanded
          @keyup.native.enter.prevent="
            customer.discount_code != '' && applyCoupon()
          "
        />
        <p class="control">
          <b-button
            type="is-primary is-custom"
            :loading="discountIsLoading"
            @click="applyCoupon"
          >
            {{ $t("apply") }}
          </b-button>
        </p>
      </b-field>
      <p v-if="discountIsApplied" class="help is-primary is-custom">
        {{ $t("promo_code_applied") }}
      </p>
    </div>
    <div v-if="canUsePayPal" class="column is-12 pb-0">
      <b-field>
        <b-radio-button
          class="w-50p"
          type="is-primary is-custom"
          v-model="hasChosenPaypal"
          :native-value="false"
        >
          <b-icon class="mr-3" icon="credit-card" />
          {{ $t("credit_card") }}
        </b-radio-button>
        <b-radio-button
          class="w-50p"
          type="is-primary is-custom"
          v-model="hasChosenPaypal"
          :native-value="true"
        >
          <b-icon class="mr-3" pack="fab" icon="paypal" />
          {{ $t("paypal") }}
        </b-radio-button>
      </b-field>
    </div>
    <div v-if="!isFree && !hasChosenPaypal" class="column is-12 pb-0">
      <b-field
        :label="$t('enter_credit_card')"
        :type="{ 'is-danger': stripeError && stripeError.message }"
        :message="stripeError && stripeError.message"
      >
        <StripeElementsSplitted
          ref="card"
          :stripe-account="store.stripe_token"
          @error="stripeError = $event"
          @complete="stripeIsCompleted = $event"
        />
      </b-field>
    </div>
    <div class="column is-12">
      <b-button
        v-if="!isFree && hasChosenPaypal"
        ref="paypal"
        type="is-black"
        class="mt-3 py-6"
        :style="{ backgroundColor: '#0070ba' }"
        icon-left="paypal"
        icon-pack="fab"
        :loading="isLoading"
        expanded
        @click="payWithPayPal"
      >
        <span v-html="$t('access_with_paypal')"></span>
      </b-button>
      <b-button
        v-else
        rel="general"
        native-type="submit"
        type="is-primary is-custom"
        class="py-6"
        :disabled="submitIsDisabled"
        :loading="isLoading"
        expanded
      >
        {{
          $t(
            buyable.buyable_type === "TRAINING"
              ? "access_training"
              : (buyable.buyable_type === "MEMBERSHIP_PRICE"
                ? "access_subscription"
                : "access_pack")
          )
        }}
      </b-button>

      <p class="mt-5 is-size-8 has-text-centered has-text-primary">
        <b-icon icon="shield-alt" />
        {{ $t("secure_registration") }}
      </p>
    </div>
  </div>
</template>
